import { useState } from 'react'
import { LumpsumRecommendationActionType } from '../../../api/common.types'
import { PlansPlanMode } from '../../../api/types'
import { showAsFormattedRupeeAmount } from '../../../../display-values/lumpsumPlanAmount'
import type { LumpsumAllocationSellPlanRecommendationDetails } from '../../api/allocationRecommendation.types'
import {
  areCounterFundsActionsCompleted,
  PlanRecommendationIntro
} from '../../shared/sheet/PlanRecommendationIntro'
import { PlanRecommendationSheetSkeleton } from '../../shared/sheet/PlanRecommendationSheetSkeleton'
import { PlansScreen } from '../../../../analytics/plansEvents'
import { PlanRecommendationSheetLayout } from '../../shared/sheet/PlanRecommendationSheetLayout'
import { PlanRecommendationStatusReasonBannerSection } from '../../shared/sheet/PlanRecommendationStatusReasonBannerSection'
import type { FundMissedGainsInfoParams } from '../../shared/missed-gains/FundMissedGainsInfoBottomSheet'
import { AllocationSellPlanRecommendationBody } from './body/AllocationSellPlanRecommendationBody'
import { AllocationSellPlanRecommendationHeader } from './header/AllocationSellPlanRecommendationHeader'

type AllocationSellPlanRecommendationSheetProps = {
  onDismiss: () => void
  isin: string
  planDetails: LumpsumAllocationSellPlanRecommendationDetails | null
  isLoading: boolean
  isFetchFailed: boolean
  onRetry: () => void
  onMissedGainsInfoClick: (params: FundMissedGainsInfoParams) => void
  transactionCta: unknown
}

export function AllocationSellPlanRecommendationSheet({
  onDismiss,
  isin,
  planDetails,
  isLoading,
  isFetchFailed,
  onRetry,
  onMissedGainsInfoClick,
  transactionCta
}: AllocationSellPlanRecommendationSheetProps) {
  const [selectedBuyIndex, setSelectedBuyIndex] = useState(0)

  // Sell-entry: root totalTransferAmount is sell; other side = sum of rounded buy card amounts.
  const sellAmount = showAsFormattedRupeeAmount({
    rawAmount: planDetails?.totalTransferAmount ?? 0
  }).value
  const buyAmount = (planDetails?.fundsToBeBought ?? []).reduce(
    (sum, fund) =>
      sum + showAsFormattedRupeeAmount({ rawAmount: fund?.buyAmount ?? 0 }).value,
    0
  )

  return (
    <PlanRecommendationSheetLayout
      onDismiss={onDismiss}
      isLoading={isLoading}
      isFetchFailed={isFetchFailed}
      onRetry={onRetry}
      planMode={PlansPlanMode.ALLOCATION}
      analyticsScreen={PlansScreen.LumpsumAllocationRecommendation}
      fundName={planDetails?.fundName ?? ''}
      fundIsin={isin}
    >
      {!isLoading ? (
        <PlanRecommendationStatusReasonBannerSection planDetails={planDetails} analyticsScreen={PlansScreen.LumpsumAllocationRecommendation} />
      ) : null}
      {isLoading ? (
        <PlanRecommendationSheetSkeleton planMode={PlansPlanMode.ALLOCATION} detailsTitle="Sell details" />
      ) : (
        <>
          <PlanRecommendationIntro
            planMode={PlansPlanMode.ALLOCATION}
            buyAmount={buyAmount}
            sellAmount={sellAmount}
            areBuyActionsCompleted={areCounterFundsActionsCompleted(planDetails?.fundsToBeBought)}
            areSellActionsCompleted={planDetails?.executed === true}
            entryAction={LumpsumRecommendationActionType.SELL}
            counterFundCount={planDetails?.fundsToBeBought?.length ?? 0}
          />
          <AllocationSellPlanRecommendationHeader
            planDetails={planDetails}
            entryIsin={isin}
            selectedBuyIndex={selectedBuyIndex}
            onSelectBuyIndex={setSelectedBuyIndex}
            transactionCta={transactionCta}
          />
          <AllocationSellPlanRecommendationBody
            planDetails={planDetails}
            selectedBuyIndex={selectedBuyIndex}
            onSelectBuyIndex={setSelectedBuyIndex}
            onMissedGainsInfoClick={onMissedGainsInfoClick}
          />
        </>
      )}
    </PlanRecommendationSheetLayout>
  )
}
